import { Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { LeadSummary } from '../../hooks/use-leads';

interface Props {
  lead: LeadSummary;
  slaHours: number | null;
  onMove: () => void;
}

export function LeadCard({ lead, slaHours, onMove }: Props): JSX.Element {
  const { t } = useTranslation();
  const sla = computeSlaState(lead.enteredStageAt, slaHours);
  return (
    <article className="group space-y-2 rounded-lg border border-surface-200 bg-white p-3 shadow-card transition-shadow hover:shadow-card-hover">
      <Link to={`/leads/${lead.id}`} className="block">
        <p className="line-clamp-2 text-sm font-medium text-surface-900 group-hover:text-brand-700">
          {lead.title}
        </p>
        {lead.company && <p className="mt-0.5 text-xs text-surface-500">{lead.company}</p>}
      </Link>
      <div className="flex items-center justify-between text-xs">
        {lead.valueCents !== null && lead.currency && (
          <span className="font-semibold text-surface-700">
            {formatMoney(lead.valueCents, lead.currency)}
          </span>
        )}
        {sla && (
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${sla.classes}`}
          >
            <Clock className="h-3 w-3" /> {sla.label}
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={onMove}
        className="w-full rounded-md border border-surface-200 px-2 py-1 text-xs font-medium text-surface-600 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"
      >
        {t('kanban.moveTo')}
      </button>
    </article>
  );
}

function formatMoney(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(0)} ${currency}`;
  }
}

function computeSlaState(
  enteredStageAt: string,
  slaHours: number | null,
): { classes: string; label: string } | null {
  if (!slaHours) return null;
  const elapsedHours = (Date.now() - new Date(enteredStageAt).getTime()) / 36e5;
  const remaining = slaHours - elapsedHours;
  if (remaining <= 0)
    return {
      classes: 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-200',
      label: `${Math.round(-remaining)}h atraso`,
    };
  if (remaining < slaHours * 0.25)
    return {
      classes: 'bg-accent-50 text-accent-700 ring-1 ring-inset ring-accent-200',
      label: `${Math.round(remaining)}h`,
    };
  return {
    classes: 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200',
    label: `${Math.round(remaining)}h`,
  };
}
